import { subDays, format } from "date-fns";
import {
  groupAlertsByType,
  countAlarmShutoffs,
  calcSessionPresenceCount,
} from "./unitEventsUtils";

/**
 * Sensor trend analytics. Compares the most recent window against the prior
 * window of equal length and classifies each metric as improving / stable /
 * declining. All functions here are pure (no React) so they can be unit-tested
 * and reused by both the Sensors table and the Sensor Detail page.
 *
 * Confirmed design: composite *safety* score drives the table column; windows
 * are 28 days vs the prior 28 days; behavioral signals (cooking frequency,
 * attendance, odd-hour cooking) are folded into the overall status and shown
 * individually on the detail page.
 */

export const WINDOW_DAYS = 28;
/** Below this combined event count a metric reads "stable": a 1 to 2 blip is
 *  +100% but statistically meaningless, so we suppress it. */
export const MIN_EVENTS = 3;
/** A metric must move at least ±25% to count as a trend. */
export const PCT_THRESHOLD = 0.25;
/** Danger weights for the composite safety score. Auto-shutoffs and immediate
 *  danger weigh double because they represent the most severe outcomes. */
export const SAFETY_WEIGHTS = {
  isUnattended: 1,
  isTooHot: 1,
  isImmediateDanger: 2,
  shutoff: 2,
};

/*
 * Graded trend plus "most problematic" priority.
 * The trend is graded into 7 levels (-3 strong improvement to +3 sharp decline)
 * and combined with the current absolute severity into a 0-100 priority score
 * used to rank which units are worst. Magnitude blends the relative change with
 * the absolute change (geometric mean) so a 1-to-3 blip (+200% but tiny) can't
 * masquerade as a sharp decline. All constants are starting values meant to be
 * calibrated against real tenant data.
 */

/** Relative change that maps to a "maxed-out" magnitude (150%). */
export const PCT_REF = 1.5;
/** Absolute-delta reference for the composite weighted safety score. */
export const ABS_REF_SCORE = 8;
/** Absolute-delta reference for raw per-alarm-type counts. */
export const ABS_REF_COUNT = 5;
/** Recent composite score that counts as fully "critical" for severity norm. */
export const SEV_REF = 14;
/** Priority blend weights (sum to 1): current severity vs. decline slope. */
export const W_SEV = 0.6;
export const W_SLOPE = 0.4;
/** Extra priority for being offline (a blind sensor can't protect anyone).
 *  Added on top of the danger blend and clamped to 100. With offlineNorm = 1
 *  (a single offline sensor, or a fully-dark property) this lands at ~45 (High);
 *  half a property offline lands at ~23 (Elevated). The caller decides how
 *  `offlineNorm` blends share-offline with absolute count. */
export const W_OFFLINE = 0.45;
/** Magnitude cutoffs for slope buckets (slight is also the stable/slight floor). */
export const MAGNITUDE_BANDS = { sharp: 0.6, moderate: 0.33, slight: 0.12 };
/** Absolute-severity band floors, on the recent composite score. */
export const SEVERITY_THRESHOLDS = { critical: 14, high: 7, elevated: 3 };
/** Priority-band floors, on the 0-100 score. */
export const PRIORITY_THRESHOLDS = { critical: 70, high: 45, elevated: 20, low: 1 };

/** Signed trend level → label + stable-relative key. Positive = worse. */
export const TREND_LEVELS = {
  "-3": { key: "strongImprove", label: "Recovering" },
  "-2": { key: "moderateImprove", label: "Improving" },
  "-1": { key: "slightImprove", label: "Easing" },
  "0": { key: "stable", label: "Steady" },
  "1": { key: "slightDecline", label: "Slipping" },
  "2": { key: "moderateDecline", label: "Worsening" },
  "3": { key: "sharpDecline", label: "Deteriorating" },
};

/**
 * Grade a recent-vs-prior change into a signed level (-3..+3). `concern` is the
 * direction that is *bad* ("up" for danger, "down" for attendance/frequency).
 * `absRef` scales the absolute-delta term (score units vs. raw counts).
 */
export function gradeTrend(recent, prior, opts = {}) {
  const {
    concern = "up",
    absRef = ABS_REF_SCORE,
    minEvents = MIN_EVENTS,
  } = opts;

  const stable = (magnitude = 0) => ({
    level: 0,
    key: "stable",
    label: TREND_LEVELS["0"].label,
    magnitude,
  });

  if ((recent || 0) + (prior || 0) < minEvents) return stable();

  const pct = pctChange(recent, prior);
  const rPct = pct === null ? 1 : Math.min(Math.abs(pct) / PCT_REF, 1);
  const rAbs = Math.min(Math.abs((recent || 0) - (prior || 0)) / absRef, 1);
  const magnitude = Math.sqrt(rPct * rAbs);

  let bucket = 0;
  if (magnitude >= MAGNITUDE_BANDS.sharp) bucket = 3;
  else if (magnitude >= MAGNITUDE_BANDS.moderate) bucket = 2;
  else if (magnitude >= MAGNITUDE_BANDS.slight) bucket = 1;

  const delta = (recent || 0) - (prior || 0);
  if (bucket === 0 || delta === 0) return stable(magnitude);

  // pct === null means prior 0 / recent > 0 → an increase.
  const increased = pct === null || delta > 0;
  const worse = concern === "up" ? increased : !increased;
  const level = worse ? bucket : -bucket;
  const t = TREND_LEVELS[String(level)];
  return { level, key: t.key, label: t.label, magnitude };
}

/** Absolute-severity band from the recent composite safety score. */
export function severityBand(score) {
  const s = Number(score) || 0;
  if (s >= SEVERITY_THRESHOLDS.critical) return { band: "critical", label: "Critical" };
  if (s >= SEVERITY_THRESHOLDS.high) return { band: "high", label: "High" };
  if (s >= SEVERITY_THRESHOLDS.elevated) return { band: "elevated", label: "Elevated" };
  if (s > 0) return { band: "low", label: "Low" };
  return { band: "none", label: "None" };
}

/** Priority band from the 0-100 score. */
export function priorityBand(score) {
  const s = Number(score) || 0;
  if (s >= PRIORITY_THRESHOLDS.critical) return { band: "critical", label: "Critical" };
  if (s >= PRIORITY_THRESHOLDS.high) return { band: "high", label: "High" };
  if (s >= PRIORITY_THRESHOLDS.elevated) return { band: "elevated", label: "Elevated" };
  if (s >= PRIORITY_THRESHOLDS.low) return { band: "low", label: "Low" };
  return { band: "none", label: "None" };
}

/**
 * Blend current severity (how bad now) with decline slope (how fast worsening)
 * into a 0-100 priority score for ranking the most problematic units. Improving
 * units contribute no slope urgency.
 */
export function computePriority(recent, prior, opts = {}) {
  const { offlineNorm = 0, ...gradeOpts } = opts;
  const g = gradeTrend(recent, prior, {
    concern: "up",
    absRef: ABS_REF_SCORE,
    ...gradeOpts,
  });
  const sevNorm = Math.min((Number(recent) || 0) / SEV_REF, 1);
  const declineNorm = g.level > 0 ? g.magnitude : 0;
  const offline = W_OFFLINE * Math.max(0, Math.min(1, offlineNorm));
  const score = Math.round(
    100 * Math.min(1, W_SEV * sevNorm + W_SLOPE * declineNorm + offline)
  );
  const band = priorityBand(score);
  return {
    score,
    band: band.band,
    label: band.label,
    level: g.level,
    levelKey: g.key,
    levelLabel: g.label,
    magnitude: g.magnitude,
  };
}
/**
 * Two contiguous, non-overlapping day-bounded windows ending today.
 * recent = [today-27 .. today], prior = [today-55 .. today-28].
 * Returned as "yyyy-MM-dd" strings to feed the existing count helpers.
 */
export function trendWindows(now = new Date()) {
  const fmt = (d) => format(d, "yyyy-MM-dd");
  return {
    recentStart: fmt(subDays(now, WINDOW_DAYS - 1)),
    recentEnd: fmt(now),
    priorStart: fmt(subDays(now, WINDOW_DAYS * 2 - 1)),
    priorEnd: fmt(subDays(now, WINDOW_DAYS)),
  };
}

/** Relative change recent-vs-prior. Returns null when there's no prior baseline
 *  but recent activity exists ("newly appeared"), 0 when both are zero. */
export function pctChange(recent, prior) {
  if (prior === 0) return recent === 0 ? 0 : null;
  return (recent - prior) / prior;
}

/**
 * Classify a count change. `concern` is the direction that is *bad*:
 *  - "up"   → an increase is declining (alarms, shutoffs, night cooking)
 *  - "down" → a decrease is declining (cooking frequency, attendance)
 * `improveOnOpposite` controls whether the opposite move reads as "improving"
 * or merely "stable" (we don't celebrate, e.g., more cooking as "improving").
 */
export function classifyChange(recent, prior, opts = {}) {
  const { improveOnOpposite = true } = opts;
  const g = gradeTrend(recent, prior, opts);
  if (g.level > 0) return "declining";
  if (g.level < 0) return improveOnOpposite ? "improving" : "stable";
  return "stable";
}

/** "up 40%", "down 25%", "newly appeared", "unchanged". */
function describeDelta(pct) {
  if (pct === null) return "newly appeared";
  if (Math.round(pct * 100) === 0) return "unchanged";
  return `${pct > 0 ? "up" : "down"} ${Math.abs(Math.round(pct * 100))}%`;
}

const round1 = (n) => Math.round(n * 10) / 10;

/**
 * Direction the raw number moved plus whether that move is favorable. `concern`
 * is the bad direction: "up" for danger metrics, "down" for things like
 * attendance / cooking frequency. Used to draw a directional arrow (not a
 * neutral one) colored green/red on the detail card.
 */
function dirTone(recent, prior, concern) {
  const delta = recent - prior;
  const dir = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const favorableDir = concern === "up" ? "down" : "up";
  const tone = dir === "flat" ? "neutral" : dir === favorableDir ? "good" : "bad";
  return { dir, tone };
}

/**
 * Build a card item for a rate metric (0..1 ratios). Compared in whole
 * percentage points. Returns a "no data" item (text only, no arrow) when either
 * window's ratio is null.
 */
function buildRateItem(key, label, recentRatio, priorRatio, concern, suffix, emptyText) {
  if (recentRatio === null || priorRatio === null) {
    return {
      key,
      label,
      status: "stable",
      hasData: false,
      dir: "flat",
      tone: "neutral",
      detail: emptyText,
    };
  }
  const rp = Math.round(priorRatio * 100);
  const rr = Math.round(recentRatio * 100);
  const dir = rr > rp ? "up" : rr < rp ? "down" : "flat";
  const favorableDir = concern === "up" ? "down" : "up";
  const tone = dir === "flat" ? "neutral" : dir === favorableDir ? "good" : "bad";
  const status =
    tone === "good" ? "improving" : tone === "bad" ? "declining" : "stable";
  return {
    key,
    label,
    status,
    hasData: true,
    dir,
    tone,
    detail: `${rp}% → ${rr}% ${suffix}`,
  };
}

/**
 * Composite safety trend from alarm history. Returns the overall status plus a
 * per-type breakdown ("items") for the detail card.
 */
export function computeSafetyTrend(alertRecords, now = new Date()) {
  const w = trendWindows(now);
  const recentByType = groupAlertsByType(alertRecords, w.recentStart, w.recentEnd);
  const priorByType = groupAlertsByType(alertRecords, w.priorStart, w.priorEnd);
  const recentShut = countAlarmShutoffs(alertRecords, w.recentStart, w.recentEnd);
  const priorShut = countAlarmShutoffs(alertRecords, w.priorStart, w.priorEnd);

  const score = (t, shut) =>
    SAFETY_WEIGHTS.isUnattended * (t.isUnattended || 0) +
    SAFETY_WEIGHTS.isTooHot * (t.isTooHot || 0) +
    SAFETY_WEIGHTS.isImmediateDanger * (t.isImmediateDanger || 0) +
    SAFETY_WEIGHTS.shutoff * shut;

  const recent = score(recentByType, recentShut);
  const prior = score(priorByType, priorShut);
  const status = classifyChange(recent, prior, { concern: "up" });

  const raw = [
    {
      key: "isUnattended",
      label: "Unattended cooking",
      recent: recentByType.isUnattended || 0,
      prior: priorByType.isUnattended || 0,
    },
    {
      key: "isTooHot",
      label: "Dangerous temperature",
      recent: recentByType.isTooHot || 0,
      prior: priorByType.isTooHot || 0,
    },
    {
      key: "isImmediateDanger",
      label: "Immediate danger",
      recent: recentByType.isImmediateDanger || 0,
      prior: priorByType.isImmediateDanger || 0,
    },
    { key: "shutoff", label: "Safety shut offs", recent: recentShut, prior: priorShut },
  ];

  const items = raw.map((c) => {
    const pct = pctChange(c.recent, c.prior);
    const graded = gradeTrend(c.recent, c.prior, {
      concern: "up",
      absRef: ABS_REF_COUNT,
    });
    return {
      key: c.key,
      label: c.label,
      status: classifyChange(c.recent, c.prior, { concern: "up" }),
      level: graded.level,
      levelKey: graded.key,
      levelLabel: graded.label,
      hasData: true,
      ...dirTone(c.recent, c.prior, "up"),
      detail: `${c.prior} → ${c.recent} (${describeDelta(pct)})`,
    };
  });

  // % resolved = danger alarms that did NOT end in an auto-shutoff (someone
  // intervened in time). Higher is better; a drop is the concern. Mirrors the
  // resolved-rate definition used in AlertTrendsCard.
  const recentAlerts =
    (recentByType.isUnattended || 0) +
    (recentByType.isTooHot || 0) +
    (recentByType.isImmediateDanger || 0);
  const priorAlerts =
    (priorByType.isUnattended || 0) +
    (priorByType.isTooHot || 0) +
    (priorByType.isImmediateDanger || 0);
  items.push(
    buildRateItem(
      "resolvedRate",
      "Resolved without shut off",
      recentAlerts > 0
        ? Math.max(0, recentAlerts - recentShut) / recentAlerts
        : null,
      priorAlerts > 0
        ? Math.max(0, priorAlerts - priorShut) / priorAlerts
        : null,
      "down",
      "resolved",
      "no alarms to resolve"
    )
  );

  // "Most problematic" priority: how bad now (recent score) blended with how
  // fast it's worsening. Drivers explain it in plain language for the card.
  const priority = computePriority(recent, prior);
  const driverParts = raw
    .filter((c) => c.recent > 0)
    .map((c) => ({
      label: c.label,
      recent: c.recent,
      weight: SAFETY_WEIGHTS[c.key] || 1,
    }))
    .sort((a, b) => b.recent * b.weight - a.recent * a.weight)
    .slice(0, 2)
    .map((c) => `${c.recent} ${c.label.toLowerCase()}`);
  if (priority.level >= 3) driverParts.push("rising sharply");
  else if (priority.level > 0) driverParts.push("rising");
  priority.drivers = driverParts;

  return {
    status,
    recent,
    prior,
    pct: pctChange(recent, prior),
    items,
    level: priority.level,
    levelKey: priority.levelKey,
    levelLabel: priority.levelLabel,
    severity: severityBand(recent),
    priority,
  };
}

export function filterSessionsByWindow(sessions, startDate, endDate) {
  if (!Array.isArray(sessions)) return [];
  const startMs = Date.parse(`${startDate}T00:00:00`);
  const endMs = Date.parse(`${endDate}T23:59:59`);
  return sessions.filter((s) => {
    if (!s || !s.dateStart) return false;
    const ts = Date.parse(String(s.dateStart).replace(" ", "T"));
    return !Number.isNaN(ts) && ts >= startMs && ts <= endMs;
  });
}

/** Sessions that started between midnight and 5am, a "sundowning" proxy. */
function countOddHourSessions(sessions) {
  let n = 0;
  for (const s of sessions) {
    if (!s || !s.dateStart) continue;
    const ts = Date.parse(String(s.dateStart).replace(" ", "T"));
    if (Number.isNaN(ts)) continue;
    const hour = new Date(ts).getHours();
    if (hour >= 0 && hour < 5) n += 1;
  }
  return n;
}

/**
 * Behavioral trends from cooking-session history. Returns one item per signal,
 * each with a status + human-readable detail line.
 */
export function computeBehavioralTrends(sessions, now = new Date()) {
  const w = trendWindows(now);
  const recentSessions = filterSessionsByWindow(sessions, w.recentStart, w.recentEnd);
  const priorSessions = filterSessionsByWindow(sessions, w.priorStart, w.priorEnd);
  const weeks = WINDOW_DAYS / 7;

  // Cooking frequency, colored by direction (more cooking = green, less = red,
  // treating a drop as the concern). Does not influence the overall verdict.
  const recentFreq = recentSessions.length / weeks;
  const priorFreq = priorSessions.length / weeks;
  const frequency = {
    key: "cookingFrequency",
    label: "Cooking frequency",
    status: "stable",
    hasData: true,
    ...dirTone(recentSessions.length, priorSessions.length, "down"),
    detail: `${round1(priorFreq)}/wk → ${round1(recentFreq)}/wk (${describeDelta(
      pctChange(recentSessions.length, priorSessions.length)
    )})`,
  };

  // Times present: total presence detections during cooking sessions. Colored
  // by direction (more presence = green, less = red); a general caregiver
  // activity indicator that doesn't influence the verdict.
  const recentPresence = calcSessionPresenceCount(recentSessions);
  const priorPresence = calcSessionPresenceCount(priorSessions);
  const presence = {
    key: "presenceCount",
    label: "Times present (while cooking)",
    status: "stable",
    hasData: true,
    ...dirTone(recentPresence, priorPresence, "down"),
    detail: `${priorPresence} → ${recentPresence} (${describeDelta(
      pctChange(recentPresence, priorPresence)
    )})`,
  };

  // Odd-hour cooking: more late-night sessions can signal sundowning.
  const recentOdd = countOddHourSessions(recentSessions);
  const priorOdd = countOddHourSessions(priorSessions);
  const oddHours = {
    key: "oddHourCooking",
    label: "Late-night cooking (12-5am)",
    status: classifyChange(recentOdd, priorOdd, { concern: "up" }),
    hasData: true,
    ...dirTone(recentOdd, priorOdd, "up"),
    detail: `${priorOdd} → ${recentOdd} (${describeDelta(
      pctChange(recentOdd, priorOdd)
    )})`,
  };

  return [frequency, presence, oddHours];
}

const RANK = { declining: 2, stable: 1, improving: 0 };

/**
 * Overall sensor trend. The verdict is driven by the composite *safety* (alarm)
 * score only. Behavioral signals are shown for context but don't move it, since
 * there's no objective "good" level for things like cooking volume. `rank` is
 * numeric so the table column can be sorted (declining sorts highest).
 */
export function computeSensorTrend(alertRecords, sessions, now = new Date(), opts = {}) {
  const { offlineNorm = 0 } = opts;
  const safety = computeSafetyTrend(alertRecords, now);
  const behavioral = computeBehavioralTrends(sessions, now);
  const status = safety.status;

  // The trend itself stays alarm-based; priority adds an offline penalty since a
  // blind sensor can't monitor or shut off the stove.
  let priority = safety.priority;
  if (offlineNorm > 0) {
    priority = computePriority(safety.recent, safety.prior, { offlineNorm });
    priority.drivers = [...(safety.priority.drivers || []), "sensor offline"];
  }

  return {
    status,
    rank: RANK[status],
    level: safety.level,
    levelKey: safety.levelKey,
    levelLabel: safety.levelLabel,
    severity: safety.severity,
    priority,
    priorityScore: priority.score,
    priorityBand: priority.band,
    safety,
    behavioral,
  };
}

/**
 * Aggregate safety trend for a building (or any group). Sums each sensor's
 * recent and prior composite safety scores, then classifies the total, so a
 * building reflects the combined danger trend of all its sensors. `recordsList`
 * is an array of per-sensor alarm-record arrays.
 */
export function computeBuildingTrend(recordsList, now = new Date(), opts = {}) {
  const { offlineNorm = 0 } = opts;
  const list = recordsList || [];
  const sensorCount = list.length || 1;
  let recent = 0;
  let prior = 0;
  let worstUnitPriority = 0;
  let worstLevel = 0;
  for (const records of list) {
    const s = computeSafetyTrend(records, now);
    recent += s.recent;
    prior += s.prior;
    if (s.priority.score > worstUnitPriority) worstUnitPriority = s.priority.score;
    if (s.priority.level > worstLevel) worstLevel = s.priority.level;
  }
  // Trend (direction) stays building-wide so the badge reflects the whole property.
  const status = classifyChange(recent, prior, { concern: "up" });
  const slope = gradeTrend(recent, prior, { concern: "up" });
  // Size-fair priority: average danger per unit (+ offline), floored by the
  // single worst unit so one critical resident in a big property still surfaces.
  const perUnit = computePriority(recent / sensorCount, prior / sensorCount, {
    offlineNorm,
  });
  const score = Math.max(perUnit.score, worstUnitPriority);
  const band = priorityBand(score);
  const priority = { ...perUnit, score, band: band.band, label: band.label };
  return {
    status,
    rank: RANK[status],
    recent,
    prior,
    level: slope.level,
    levelKey: slope.key,
    levelLabel: slope.label,
    severity: severityBand(recent / sensorCount),
    priority,
    priorityScore: score,
    priorityBand: band.band,
    worstUnitPriority,
    worstLevel,
  };
}
